import React, { useState, useEffect, useCallback } from 'react';
import { X, Volume2, Brain, CheckCircle, RotateCcw, TrendingUp, Clock, Zap, Award, Wifi, WifiOff, Download, RefreshCw, CloudOff } from 'lucide-react';
import { useOfflineSRS } from '../../hooks/useOfflineSRS';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quality button component
const QualityButton = ({ quality, label, color, onClick, disabled }) => (
  <button
    onClick={() => onClick(quality)}
    disabled={disabled}
    className={`flex-1 py-3 px-2 rounded-lg font-medium text-sm transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${color}`}
    data-testid={`srs-quality-${quality}`}
  >
    {label}
  </button>
);

// Stats card component
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className={`bg-gray-800/50 rounded-lg p-3 text-center ${color}`}>
    <Icon className="w-5 h-5 mx-auto mb-1" />
    <div className="text-lg font-bold">{value}</div>
    <div className="text-xs text-gray-400">{label}</div>
  </div>
);

// Connection status badge
const ConnectionStatus = ({ isOnline, isOfflineMode, pendingCount, isSyncing, onSync, onDownload }) => (
  <div className="flex items-center gap-2 text-xs">
    {isOnline ? (
      <span className="flex items-center gap-1 text-green-400">
        <Wifi className="w-3 h-3" />
        En ligne
      </span>
    ) : (
      <span className="flex items-center gap-1 text-orange-400">
        <WifiOff className="w-3 h-3" />
        Hors ligne
      </span>
    )}
    
    {isOfflineMode && (
      <span className="flex items-center gap-1 text-purple-400 bg-purple-500/20 px-2 py-0.5 rounded-full">
        <CloudOff className="w-3 h-3" />
        Mode local
      </span>
    )}
    
    {pendingCount > 0 && (
      <span className="flex items-center gap-1 text-yellow-400 bg-yellow-500/20 px-2 py-0.5 rounded-full">
        {pendingCount} en attente
      </span>
    )}
    
    {isOnline && pendingCount > 0 && !isSyncing && (
      <button
        onClick={onSync}
        className="flex items-center gap-1 text-blue-400 hover:text-blue-300"
        title="Synchroniser"
      >
        <RefreshCw className="w-3 h-3" />
      </button>
    )}
    
    {isOnline && (
      <button
        onClick={onDownload}
        className="flex items-center gap-1 text-gray-400 hover:text-gray-300"
        title="Télécharger pour hors-ligne"
      >
        <Download className="w-3 h-3" />
      </button>
    )}
  </div>
);

const SRSModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [stats, setStats] = useState(null);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [reviewedCards, setReviewedCards] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(null);

  // Use offline SRS hook
  const {
    isOnline,
    isOfflineReady,
    isSyncing,
    pendingCount,
    lastSync,
    downloadCardsForOffline,
    getCards,
    getStats,
    submitReview,
    syncPendingReviews
  } = useOfflineSRS();

  // Fetch cards and stats (online or offline)
  const fetchData = useCallback(async () => {
    if (!isOfflineReady) return;
    
    setLoading(true);
    try {
      const [cardsResult, statsResult] = await Promise.all([
        getCards(20),
        getStats()
      ]);
      
      setCards(cardsResult.cards || []);
      setStats(statsResult);
      setIsOfflineMode(cardsResult.isOffline || statsResult.isOffline);
      setCurrentIndex(0);
      setShowAnswer(false);
      setSessionComplete((cardsResult.cards || []).length === 0);
      setReviewedCards([]);
    } catch (error) {
      console.error('Error fetching SRS data:', error);
    } finally {
      setLoading(false);
    }
  }, [isOfflineReady, getCards, getStats]);

  useEffect(() => {
    if (isOpen && isOfflineReady) {
      fetchData();
    }
  }, [isOpen, isOfflineReady, fetchData]);

  // Download cards for offline
  const handleDownload = async () => {
    setDownloadProgress('Téléchargement...');
    const success = await downloadCardsForOffline();
    if (success) {
      setDownloadProgress('✓ Téléchargé !');
      setTimeout(() => setDownloadProgress(null), 2000);
      // Refresh data
      fetchData();
    } else {
      setDownloadProgress('✗ Erreur');
      setTimeout(() => setDownloadProgress(null), 2000);
    }
  };

  // Play audio for current card - iPad Safari compatible (axios + blob)
  const playAudio = async () => {
    if (!cards[currentIndex] || isPlaying) return;
    
    setIsPlaying(true);
    try {
      // Use axios with responseType blob - compatible with iPad Safari
      const response = await axios.post(
        `${API_URL}/api/tts?text=${encodeURIComponent(cards[currentIndex].thai)}&speed=0.75`,
        null,
        { responseType: 'blob', timeout: 30000 }
      );
      
      // Verify we got audio data
      if (!response.data || response.data.size === 0) {
        throw new Error("Empty audio response");
      }
      
      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        setIsPlaying(false);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl);
        setIsPlaying(false);
      };
      
      await audio.play();
    } catch (error) {
      console.error('Error playing audio:', error);
      setIsPlaying(false);
    }
  };

  // Submit review (online or offline)
  const handleSubmitReview = async (quality) => {
    const card = cards[currentIndex];
    if (!card) return;

    try {
      const result = await submitReview(card.id, quality);
      setFeedback(result);
      setReviewedCards([...reviewedCards, { ...card, quality, result }]);
      
      // Move to next card after short delay
      setTimeout(() => {
        setFeedback(null);
        if (currentIndex < cards.length - 1) {
          setCurrentIndex(currentIndex + 1);
          setShowAnswer(false);
        } else {
          setSessionComplete(true);
          // Refresh stats
          getStats().then(setStats);
        }
      }, 1500);
      
    } catch (error) {
      console.error('Error submitting review:', error);
    }
  };

  // Calculate session stats
  const getSessionStats = () => {
    if (reviewedCards.length === 0) return null;
    
    const correct = reviewedCards.filter(c => c.quality >= 3).length;
    const accuracy = (correct / reviewedCards.length) * 100;
    const avgQuality = reviewedCards.reduce((sum, c) => sum + c.quality, 0) / reviewedCards.length;
    
    return { reviewed: reviewedCards.length, correct, accuracy, avgQuality };
  };

  if (!isOpen) return null;

  const currentCard = cards[currentIndex];
  const sessionStats = getSessionStats();

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl border border-purple-500/30" data-testid="srs-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <Brain className="w-6 h-6 text-white" />
              <div>
                <h2 className="text-xl font-bold text-white">Révision Espacée</h2>
                <p className="text-purple-200 text-sm">Algorithme SM-2</p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="text-white/80 hover:text-white transition-colors"
              data-testid="srs-close-btn"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Connection status */}
          <div className="mt-3 flex items-center justify-between">
            <ConnectionStatus 
              isOnline={isOnline}
              isOfflineMode={isOfflineMode}
              pendingCount={pendingCount}
              isSyncing={isSyncing}
              onSync={syncPendingReviews}
              onDownload={handleDownload}
            />
            {downloadProgress && (
              <span className="text-xs text-purple-200">{downloadProgress}</span>
            )}
          </div>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mb-4"></div>
              <p className="text-gray-400">Chargement des cartes...</p>
            </div>
          ) : sessionComplete ? (
            /* Session Complete View */
            <div className="text-center py-8">
              <div className="mb-6">
                <Award className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">Session terminée ! 🎉</h3>
                {sessionStats ? (
                  <div className="space-y-4">
                    <p className="text-gray-300">
                      Tu as révisé <span className="text-purple-400 font-bold">{sessionStats.reviewed}</span> cartes
                      {isOfflineMode && <span className="text-orange-400"> (mode hors-ligne)</span>}
                    </p>
                    <div className="grid grid-cols-2 gap-4 max-w-xs mx-auto">
                      <div className="bg-green-500/20 rounded-lg p-3">
                        <div className="text-2xl font-bold text-green-400">{sessionStats.correct}</div>
                        <div className="text-xs text-gray-400">Correctes</div>
                      </div>
                      <div className="bg-purple-500/20 rounded-lg p-3">
                        <div className="text-2xl font-bold text-purple-400">{sessionStats.accuracy.toFixed(0)}%</div>
                        <div className="text-xs text-gray-400">Précision</div>
                      </div>
                    </div>
                    
                    {/* Pending sync warning */}
                    {pendingCount > 0 && (
                      <div className="bg-yellow-500/20 rounded-lg p-3 text-yellow-400 text-sm">
                        <p>⚠️ {pendingCount} révision(s) en attente de synchronisation</p>
                        {isOnline && (
                          <button 
                            onClick={syncPendingReviews}
                            className="mt-2 flex items-center gap-2 mx-auto text-yellow-300 hover:text-yellow-200"
                          >
                            <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
                            {isSyncing ? 'Synchronisation...' : 'Synchroniser maintenant'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-400">Toutes les cartes sont à jour !</p>
                )}
              </div>
              
              {/* Updated Stats */}
              {stats && (
                <div className="grid grid-cols-4 gap-2 mb-6">
                  <StatCard icon={CheckCircle} label="Maîtrisés" value={stats.cards_learned} color="text-green-400" />
                  <StatCard icon={TrendingUp} label="En cours" value={stats.cards_learning} color="text-blue-400" />
                  <StatCard icon={Zap} label="Nouveaux" value={stats.cards_new} color="text-yellow-400" />
                  <StatCard icon={Clock} label="Série" value={`${stats.streak_days || 0}j`} color="text-purple-400" />
                </div>
              )}
              
              <div className="flex gap-3 justify-center">
                <button
                  onClick={fetchData}
                  className="flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                  data-testid="srs-restart-btn"
                >
                  <RotateCcw className="w-4 h-4" />
                  Nouvelle session
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          ) : currentCard && (
            /* Review Card View */
            <div className="space-y-4">
              {/* Progress bar */}
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-purple-500 to-indigo-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((currentIndex) / cards.length) * 100}%` }}
                  />
                </div>
                <span className="text-sm text-gray-400">{currentIndex + 1}/{cards.length}</span>
              </div>

              {/* Stats row */}
              {stats && (
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="bg-gray-800/50 rounded p-2">
                    <div className="text-green-400 font-bold">{stats.cards_learned}</div>
                    <div className="text-xs text-gray-500">Maîtrisés</div>
                  </div>
                  <div className="bg-gray-800/50 rounded p-2">
                    <div className="text-blue-400 font-bold">{stats.cards_learning}</div>
                    <div className="text-xs text-gray-500">En cours</div>
                  </div>
                  <div className="bg-gray-800/50 rounded p-2">
                    <div className="text-yellow-400 font-bold">{stats.cards_new}</div>
                    <div className="text-xs text-gray-500">Nouveaux</div>
                  </div>
                  <div className="bg-gray-800/50 rounded p-2">
                    <div className="text-purple-400 font-bold">{stats.accuracy_rate?.toFixed(0) || 0}%</div>
                    <div className="text-xs text-gray-500">Précision</div>
                  </div>
                </div>
              )}

              {/* Card */}
              <div 
                className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 text-center border border-gray-700 min-h-[200px] flex flex-col justify-center cursor-pointer hover:border-purple-500/50 transition-colors"
                onClick={() => !showAnswer && setShowAnswer(true)}
                data-testid="srs-card"
              >
                {/* Thai word */}
                <div className="mb-4">
                  <span className="text-xs text-purple-400 uppercase tracking-wider">{currentCard.category}</span>
                  <h3 className="text-4xl font-bold text-white mt-2 mb-2">{currentCard.thai}</h3>
                  <button
                    onClick={(e) => { e.stopPropagation(); playAudio(); }}
                    disabled={isPlaying}
                    className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm transition-colors ${
                      isPlaying 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                    data-testid="srs-audio-btn"
                    title="Écouter"
                  >
                    <Volume2 className={`w-4 h-4 ${isPlaying ? 'animate-pulse' : ''}`} />
                    {isPlaying ? 'Lecture...' : 'Écouter'}
                  </button>
                </div>

                {/* Answer */}
                {showAnswer ? (
                  <div className="border-t border-gray-700 pt-4 mt-4">
                    <p className="text-2xl text-green-400 font-semibold">{currentCard.french}</p>
                    <div className="flex items-center justify-center gap-4 mt-3 text-xs text-gray-500">
                      <span>Intervalle: {currentCard.interval}j</span>
                      <span>•</span>
                      <span>Facilité: {currentCard.ease_factor?.toFixed(1)}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm mt-4">
                    Tape pour voir la réponse
                  </p>
                )}
              </div>

              {/* Feedback */}
              {feedback && (
                <div className={`p-3 rounded-lg text-center ${
                  feedback.new_interval > 1 ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'
                }`}>
                  {feedback.feedback}
                  <div className="text-xs mt-1 opacity-75">
                    Prochaine révision: {feedback.next_review}
                    {feedback.isOffline && ' (hors-ligne)'}
                  </div>
                </div>
              )}

              {/* Quality buttons */}
              {showAnswer && !feedback && (
                <div className="space-y-3">
                  <p className="text-center text-gray-400 text-sm">Comment était ta réponse ?</p>
                  <div className="grid grid-cols-3 gap-2">
                    <QualityButton
                      quality={0}
                      label="😰 Oublié"
                      color="bg-red-600 hover:bg-red-700 text-white"
                      onClick={handleSubmitReview}
                    />
                    <QualityButton
                      quality={2}
                      label="😕 Difficile"
                      color="bg-orange-600 hover:bg-orange-700 text-white"
                      onClick={handleSubmitReview}
                    />
                    <QualityButton
                      quality={3}
                      label="🤔 Correct"
                      color="bg-yellow-600 hover:bg-yellow-700 text-white"
                      onClick={handleSubmitReview}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <QualityButton
                      quality={4}
                      label="😊 Facile"
                      color="bg-green-600 hover:bg-green-700 text-white"
                      onClick={handleSubmitReview}
                    />
                    <QualityButton
                      quality={5}
                      label="🎉 Parfait !"
                      color="bg-purple-600 hover:bg-purple-700 text-white"
                      onClick={handleSubmitReview}
                    />
                  </div>
                </div>
              )}

              {/* Show answer button */}
              {!showAnswer && (
                <button
                  onClick={() => setShowAnswer(true)}
                  className="w-full py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors"
                  data-testid="srs-show-answer-btn"
                >
                  Afficher la réponse
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SRSModal;
